import { useCallback, useEffect, useState } from "react"
import FolderIcon from "../../../../media/FolderIcon"
import { CladdingMaterial, InsulationMaterial, ScopeQRWorkflow, ScopeWorkflow, WorkflowLocation } from "../../../../types/types"
import { FileExplorerDataProps } from "../../WorkflowFileExplorer.types"
import ScopeWorkflowsSection from "./ScopeWorkflows"
import { addScopeWorkflow, deleteScopeWorkflow, getScopeWorkflows, updateScopeWorkflow } from "../../../../service/scopeWorkflowService"
import ScopeWorkflowFormType from "../../../../utils/ScopeWorkflowFormType"
import { getCladdingMaterials } from "../../../../service/claddingMaterialService"
import { getInsulationMaterials } from "../../../../service/insulationMaterialService"
import { getLocations } from "../../../../service/workflowLocationService"
import SkeletonLoadingView from "../../../../components/SkeletonLoadingView/SkeletonLoadingView"
import { showErrorToast, showLoadingToast, showSuccessToast } from "../../../../utils/utils"
import QRIcon from "../../../../media/QRIcon"
import { MAX_QR_CODE_CHARS } from "../../../../utils/constants"
import { get } from "http"
import { Id, toast } from "react-toastify"

const useScopeWorkflowsSection = (props: FileExplorerDataProps) => {
  const { limit, skip, onError, searchQuery } = props

  const [workflows, setWorkflows] = useState<ScopeWorkflow[]>([])
  const [claddingMaterials, setCladdingMaterials] = useState<string[]>([])
  const [insulationMaterials, setInsulationMaterials] = useState<string[]>([])
  const [locations, setLocations] = useState<string[]>([])
  const [selectedWorkflows, setSelectedWorkflows] = useState<number[]>([])
  const [selectedWorkflow, setSelectedWorkflow] = useState<ScopeWorkflow|undefined>(undefined)
  const [showQRCode, setShowQRCode] = useState<boolean>(false)
  const [showAddStageWorkflow, setShowAddStageWorkflow] = useState<boolean>(false)
  const [isSavingWorkflow, setIsSavingWorkflow] = useState<boolean>(false)
  const [selectedFormType, setSelectedFormType] = useState<ScopeWorkflowFormType>(ScopeWorkflowFormType.ADD)
  const [scopeQRString, setScopeQRString] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loadingToastId, setLoadingToastId] = useState<Id|undefined>(undefined)
  const [error, setError] = useState<Error|undefined>(undefined)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false)
  const [canLoadMore, setCanLoadMore] = useState<boolean>(true)

  useEffect(() => {
    if (!isLoading && loadingToastId !== undefined) {
      toast.done(loadingToastId)
    }
  }, [isLoading, loadingToastId])

  const checkError = useCallback((response: any) => {
    setIsSavingWorkflow(false)
    if (response instanceof Error) {      
      setIsLoading(false)
      onError(response)
      setError(response)
      setCanLoadMore(false)
      return true
    }
    return false
  }, [onError])

  const onRefresh = useCallback(async () => {
    setIsLoading(true)
    const workflowResponse = await getScopeWorkflows(limit, skip)
    if (checkError(workflowResponse)) { return }
    const fetchedWorkflows = workflowResponse as ScopeWorkflow[]
    setWorkflows(fetchedWorkflows)
    setCanLoadMore(fetchedWorkflows.length >= limit)

    const claddingMaterialResponse = await getCladdingMaterials()
    if (checkError(claddingMaterialResponse)) { return }
    const cladding = claddingMaterialResponse as CladdingMaterial[]
    setCladdingMaterials(cladding.map((material) => material.material))

    const insulationMaterialResponse = await getInsulationMaterials()
    if (checkError(insulationMaterialResponse)) { return }
    const insulation = insulationMaterialResponse as InsulationMaterial[]
    setInsulationMaterials(insulation.map((material) => material.material))

    const locationResponse = await getLocations()
    if (checkError(locationResponse)) { return }
    const workflowLocations = locationResponse as WorkflowLocation[]
    setLocations(workflowLocations.map((location) => location.name))

    setIsLoading(false)
  }, [limit, skip, checkError])

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  const onCheckWorkflow = (index: number) => {
    console.log(getQRCodeString(selectedWorkflows))
    
    if (selectedWorkflows.includes(index)) {
      const selected = selectedWorkflows.filter((i) => i !== index)
      setScopeQRString(getQRCodeString(selectedWorkflows))
      setSelectedWorkflows(selected)
    } else {
      const selected = [...selectedWorkflows, ...[index]]
      const workflowString = getQRCodeString(selected)
      if (workflowString.length > MAX_QR_CODE_CHARS) { return }
      setScopeQRString(workflowString)
      setSelectedWorkflows([...selectedWorkflows, ...[index]])
    }
  }

  const getQRCodeString = (workflowIndexs: number[]) => {
    const workflowsToGenerate = workflows.filter((_, index) => workflowIndexs.includes(index))
    return JSON.stringify(workflowsToGenerate.map((workflow) => {
      const qr: ScopeQRWorkflow = {
        id: workflow.pipe_id,
        t: workflow.pipe_temp,
        d: workflow.pipe_diameter,
        l: workflow.location,
        ln: workflow.pipe_length,
        s: workflow.start,
        e: workflow.end,
        tp: workflow.asset_type,
        im: workflow.insulation_material,
        it: workflow.insulation_thickness,
        cm: workflow.cladding_material,
        ct: workflow.cladding_thickness,
        st: JSON.parse(workflow.sections),
        ag: JSON.parse(workflow.angles)
      }
      return qr
    }))
  }

  const openScopeWorkflow = (formType: ScopeWorkflowFormType, workflow?: ScopeWorkflow) => {
    setSelectedWorkflow(workflow)
    setSelectedFormType(formType)
    setShowAddStageWorkflow(true)
  }

  const onSubmitScopeWorkflowForm = (workflowObject: ScopeWorkflow) => {
    if (selectedFormType === ScopeWorkflowFormType.ADD) {
      onAddWorkflow(workflowObject)
      return
    }
    if (selectedFormType === ScopeWorkflowFormType.EDIT) {
      onEditWorkflow(workflowObject)
      return
    }
  }

  const onAddWorkflow = async (workflowObject: ScopeWorkflow) => {
    setIsSavingWorkflow(true)
    const temp = {...workflowObject, filename: ""}
    setIsLoading(true)
    setLoadingToastId(showLoadingToast(`Saving ${workflowObject.pipe_id}.`))
    const workflowResponse = await addScopeWorkflow(temp)
    setIsLoading(false)
    if (checkError(workflowResponse)) { return }

    setIsSavingWorkflow(false)
    setShowAddStageWorkflow(false)
    showSuccessToast(`${workflowObject.pipe_id} created successfully.`)
    onRefresh()
  }

  const onDuplicateWorkflow = async (workflowObject: ScopeWorkflow) => {
    setIsSavingWorkflow(true)

    const newWorkflow = {
        ...workflowObject,
        pipe_id: `${workflowObject.pipe_id} (copy)`, // Append (copy) to the pipe_id
        date: `${Math.floor(Date.now() / 1000)}` // Update date to current timestamp
    }

    setIsLoading(true)
    setLoadingToastId(showLoadingToast(`Duplicating ${workflowObject.pipe_id}.`))
    const workflowResponse = await addScopeWorkflow(newWorkflow)
    setIsLoading(false)
    if (checkError(workflowResponse)) { return }

    setIsSavingWorkflow(false)
    setShowAddStageWorkflow(false)
    showSuccessToast(`${workflowObject.pipe_id} duplicated successfully.`)
    onRefresh()
  }

  const onEditWorkflow = async (workflowObject: ScopeWorkflow) => {
    setIsSavingWorkflow(true)
    const temp = {...workflowObject, filename: ""}
    setIsLoading(true)
    setLoadingToastId(showLoadingToast(`Updating ${workflowObject.pipe_id}.`))
    const workflowResponse = await updateScopeWorkflow(temp)
    setIsLoading(false)
    if (checkError(workflowResponse)) { return }

    setIsSavingWorkflow(false)
    setShowAddStageWorkflow(false)
    showSuccessToast(`${workflowObject.pipe_id} updated successfully.`)
    onRefresh()
  }

  const onDeleteWorkflow = async () => {
    setShowDeleteConfirmation(false)
    if (selectedWorkflow === undefined) {
      showErrorToast("Failed to delete workflow.")
      return
    }
    setIsLoading(true)
    setLoadingToastId(showLoadingToast(`Deleting ${selectedWorkflow.pipe_id}.`))
    const response = await deleteScopeWorkflow(selectedWorkflow.id)
    setIsLoading(false)

    if (checkError(response)) { return }
    showSuccessToast(`${selectedWorkflow.pipe_id} deleted successfully.`)
    
    onRefresh()
  }

  const onShowScopeWorkflowForm= () => {
    if (claddingMaterials.length === 0) {
      onError(new Error("Failed to fetch cladding materials. Cannot create scope workflow."))
      return
    }

    if (insulationMaterials.length === 0) {
      onError(new Error("Failed to fetch insulation materials. Cannot create scope workflow."))
      return
    }

    if (locations.length === 0) {
      onError(new Error("No locations found. Please add locations before creating a scope workflow."))
      return
    }
    openScopeWorkflow(ScopeWorkflowFormType.ADD)
  }

  const onShowQR = () => {
    if (selectedWorkflows.length === 0) {
      showErrorToast("No scope workflow selected")
    }
    setShowQRCode(selectedWorkflows.length > 0)
  }

  const canGenerateQRCode = (index: number) => {
    const selected = [...selectedWorkflows, ...[index]]
    const workflowString = getQRCodeString(selected)
    return workflowString.length < MAX_QR_CODE_CHARS
  }

  return [{
    isSavingWorkflow,
    fileExplorerOption: {
      option: {
        icon: <FolderIcon />,
        label: "Scope Workflows",
        onAdd: () => onShowScopeWorkflowForm(),
        onAddLabel: "Create workflow scope"
      },
      topBarItem: {
        icon: <div className="qr-icon"><QRIcon /></div>,
        onClick: onShowQR
      },
      component: <ScopeWorkflowsSection
                    workflows={workflows.filter((workflow) => workflow.pipe_id.toLowerCase().includes(searchQuery.toLowerCase()))}
                    claddingMaterials={claddingMaterials}
                    insulationMaterials={insulationMaterials}
                    locations={locations}
                    selectedWorkflows={selectedWorkflows}
                    showQRCode={showQRCode}
                    showAddStageWorkflow={showAddStageWorkflow}
                    selectedWorkflow={selectedWorkflow}
                    formType={selectedFormType}
                    onError={onError}
                    scopeQRString={scopeQRString}
                    onHideStageWorkflow={() => setShowAddStageWorkflow(false)}
                    onHideQRCode={() => setShowQRCode(false)}
                    onCheckWorkflow={onCheckWorkflow}
                    onOpenWorkflow={openScopeWorkflow}
                    onSubmit={onSubmitScopeWorkflowForm}
                    onDeleteWorkflow={onDeleteWorkflow}
                    error={error}
                    onSelectWorkflow={setSelectedWorkflow}
                    showDeleteConfirmation={showDeleteConfirmation}
                    onDuplicateWorkflow={onDuplicateWorkflow}
                    onShowDeleteConfirmation={setShowDeleteConfirmation}
                    canGenerateQRCode={canGenerateQRCode}
                  />,
      onRefresh: onRefresh,
      filterOptions: <p>Filter</p>,
      sortOptions: <p>Sort</p>,
      canLoadMore: canLoadMore,
      isLoading: isLoading,
      loadingView: <>
        <div className="staged-workflows-header-container content-loading-view">
          <p className="workflows-result-header">PIPE ID</p>
          <p className="workflows-result-header">SECTIONS</p>
          <p className="workflows-result-header">DATE</p>
          <p className="workflows-result-header"></p>
          {[...Array(5)].map((_) => (
            <>
              <SkeletonLoadingView />
              <SkeletonLoadingView />
              <SkeletonLoadingView style={{gridColumn: "span 2"}} />
            </>
          ))}
        </div>
      </>
    }
  }]

}

export default useScopeWorkflowsSection